<script lang="ts">
  import { localDay } from '../format';
  import { monthGrid, sameDay } from './week';

  // `busy` holds localDay keys; null while that month's meetings are still being read.
  let { month, today, busy, failed = false, isSelected, onpick, onmonth }: {
    month: Date; today: Date; busy: Set<string> | null; failed?: boolean;
    isSelected: (day: Date) => boolean; onpick: (day: Date) => void; onmonth: (delta: number) => void;
  } = $props();

  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const days = $derived(monthGrid(month));
  const title = $derived(month.toLocaleDateString([], { month: 'long', year: 'numeric' }));
  const full = (day: Date) => day.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
</script>

<div class="mm" role="dialog" aria-label="Go to date">
  <div class="mm-head">
    <button type="button" class="mm-nav" aria-label="Previous month" onclick={() => onmonth(-1)}>‹</button>
    <span class="mm-title" aria-live="polite">{title}</span>
    <button type="button" class="mm-nav" aria-label="Next month" onclick={() => onmonth(1)}>›</button>
  </div>
  <div class="mm-grid">
    {#each WEEKDAYS as weekday (weekday)}<span class="mm-wd" aria-hidden="true">{weekday.slice(0, 1)}</span>{/each}
    {#each days as day (day.getTime())}
      {@const has = !!busy?.has(localDay(day))}
      <button type="button" class="mm-day" class:out={day.getMonth() !== month.getMonth()}
        class:today={sameDay(day, today)} class:sel={isSelected(day)} class:busy={has}
        aria-current={sameDay(day, today) ? 'date' : undefined}
        aria-label="{full(day)}{has ? ', LP meetings' : ''}" onclick={() => onpick(day)}>{day.getDate()}</button>
    {/each}
  </div>
  <p class="mm-key">{#if failed}LP meetings couldn’t be read for this month.{:else if busy}<span class="mm-dot" aria-hidden="true"></span> Day with an LP meeting{:else}Reading LP meetings…{/if}</p>
</div>

<style>
  .mm { width: 252px; box-sizing: border-box; padding: 10px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-md); }
  .mm-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
  .mm-title { font-size: 13px; font-weight: 600; color: var(--text); }
  .mm-nav { width: 26px; height: 26px; justify-content: center; padding: 0; background: none; border: 0; color: var(--text-muted); font-size: 15px; }
  .mm-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
  .mm-wd { text-align: center; font-family: var(--font-mono); font-size: 9.5px; color: var(--text-dim); padding: 2px 0 4px; }
  .mm-day { position: relative; justify-content: center; height: 30px; padding: 0; background: none; border: 1px solid transparent; border-radius: var(--radius-sm); font-size: 12px; font-weight: 400; color: var(--text-muted); }
  .mm-day:hover { background: var(--bg-hover); border-color: transparent; }
  .mm-day.out { color: var(--text-dim); opacity: .55; }
  .mm-day.sel { background: var(--bg-hover); color: var(--text); }
  .mm-day.today { border-color: var(--border-strong); color: var(--text); }
  .mm-day.busy { font-weight: 700; color: var(--text); }
  .mm-day.busy::after { content: ''; position: absolute; left: 50%; bottom: 3px; width: 4px; height: 4px; margin-left: -2px; border-radius: 50%; background: var(--text-muted); }
  .mm-key { display: flex; align-items: center; gap: 6px; margin: 8px 2px 0; font-size: 11px; color: var(--text-dim); }
  .mm-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--text-muted); }
</style>
